import { Injectable, computed, signal } from '@angular/core';

/** Bump when privacy / cookie wording changes meaningfully so users see the banner again */
export const COOKIE_CONSENT_POLICY_VERSION = 1;

export type CookieConsentChoice = 'all' | 'necessary';

export interface CookieConsentRecord {
  version: number;
  choice: CookieConsentChoice;
  updatedAt: string;
}

const STORAGE_KEY = 'livepass_cookie_consent';

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly _record = signal<CookieConsentRecord | null>(this.readFromStorage());

  /** Current stored consent (null = never decided) */
  readonly record = this._record.asReadonly();

  /** Show banner until user chooses, or stored version is older than policy */
  readonly showBanner = computed(() => {
    const r = this._record();
    if (!r) return true;
    return r.version < COOKIE_CONSENT_POLICY_VERSION;
  });

  /** Use before loading non-essential scripts (analytics, ads, etc.) */
  allowsOptionalCookies(): boolean {
    const r = this._record();
    return r != null && r.version >= COOKIE_CONSENT_POLICY_VERSION && r.choice === 'all';
  }

  /** Strict mode: only cookies required for the site to function */
  isNecessaryOnly(): boolean {
    const r = this._record();
    return (
      r != null &&
      r.version >= COOKIE_CONSENT_POLICY_VERSION &&
      r.choice === 'necessary'
    );
  }

  acceptAll(): void {
    this.persist({
      version: COOKIE_CONSENT_POLICY_VERSION,
      choice: 'all',
      updatedAt: new Date().toISOString()
    });
  }

  acceptNecessaryOnly(): void {
    this.persist({
      version: COOKIE_CONSENT_POLICY_VERSION,
      choice: 'necessary',
      updatedAt: new Date().toISOString()
    });
  }

  /** For tests or “reset” in settings later */
  clearForTesting(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* SSR or private mode */
    }
    this._record.set(null);
  }

  private readFromStorage(): CookieConsentRecord | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CookieConsentRecord;
      if (
        parsed &&
        typeof parsed.version === 'number' &&
        (parsed.choice === 'all' || parsed.choice === 'necessary')
      ) {
        return parsed;
      }
    } catch {
      /* invalid */
    }
    return null;
  }

  private persist(record: CookieConsentRecord): void {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
      } catch {
        /* quota / private mode */
      }
    }
    this._record.set(record);
  }
}
